import {styled} from '@linaria/react';
import React, {FC} from 'react';
import {EllipseGlow, buildSpotlightGlowsCss} from './glows';

export interface SpotlightSlideAmbientBackgroundProps {
  isActive: boolean;
  /** Solid base color (hex) behind the glow blobs. */
  backgroundColor: string;
  /**
   * FE-owned glow positions. The caller picks the breakpoint set
   * (`SPOTLIGHT_GLOWS_MOBILE` vs `SPOTLIGHT_GLOWS_DESKTOP`).
   */
  glows: EllipseGlow[];
  /**
   * `[inner, outer?]` stop pair of the shared glow gradient as ready-to-use
   * CSS color strings (e.g. `rgba(...)`), applied to every glow slot.
   * Empty/undefined ⇒ solid base color only, no glow layer.
   */
  glowColors?: string[];
  className?: string;
}

/**
 * Ambient (slider-wide) background for the unified Spotlight `slide` type.
 * Owns the solid base colour + optional full-bleed glow so blooms can overflow
 * the slide tile the way they do in Figma. Media stays on the card. Crossfades
 * with the same active opacity transition as {@link BackgroundCard}.
 */
export const SpotlightSlideAmbientBackground: FC<
  SpotlightSlideAmbientBackgroundProps
> = ({isActive, backgroundColor, glows, glowColors, className}) => {
  const hasGlow = Boolean(glowColors && glowColors.length > 0);

  return (
    <li data-active={isActive} data-testid="spotlightSlideAmbientBackground">
      <Root className={className}>
        <BaseLayer
          style={{backgroundColor}}
          data-testid="spotlight-background-base"
        />
        {hasGlow && (
          <GlowLayer
            style={{backgroundImage: buildSpotlightGlowsCss(glows, glowColors)}}
            data-testid="spotlight-background-glow"
          />
        )}
      </Root>
    </li>
  );
};

const Root = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  opacity: 0;
  transition: opacity 500ms ease-in-out 250ms;

  [data-active='true'] & {
    transition-delay: 0ms;
    opacity: 1;
  }
`;

const BaseLayer = styled.div`
  position: absolute;
  inset: 0;
`;

const GlowLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;
